import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, FormControl, Grid, Typography } from '@mui/material';
import produce from 'immer';
import { IClientDeleteInputModel } from 'inputModels';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import Modal from 'react-modal';
import { IClientViewModel, ISolicitationViewModel } from 'viewModels';
import { useTheme } from 'styled-components';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/IconDelete.svg';
import { ReactComponent as ExpandArrowIcon } from '../../../assets/icons/iconExpandArrow.svg';
import { useClients } from '../../../context/clients';
import { Button } from '../../Buttons/Button';
import { CardOpenSolicitation } from '../../Cards/CardOpenSolicitation';
import { SelectControled } from '../../SelectControled';
import { ContentModal, Header } from './style';
import { CompanyController } from '../../../controller/CompanyController';

interface ModalDeleteClientProps {
  isOpen: boolean;
  currentClientSelected: IClientViewModel;
  onRequestClose: () => void;
  onRequestRemoveClient: (clientDeleted: IClientDeleteInputModel) => void;
}
export function ModalDeleteClient({
  isOpen,
  currentClientSelected,
  onRequestClose,
  onRequestRemoveClient,
}: ModalDeleteClientProps) {
  const theme = useTheme();
  const { clients, setEnableClientsLoad } = useClients();
  const [solicitationsByClient, setSolicitationsByClient] = useState<ISolicitationViewModel[]>([]);
  const [searchingSolicitation, setSearchingSolicitation] = useState<boolean>(true);
  const [hasHoverCard, setHasHoverCard] = useState<boolean[][]>(() => {
    return Array(...Array(24)).map(() => {
      return Array(...Array(31)).map(() => false);
    });
  });

  const [defaultValues] = useState<IClientViewModel>({
    id: '',
    name: '',
  });
  const { control, reset } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });

  const [clientId] = useWatch({
    control,
    name: ['id'],
  });

  const MenuProps = {
    PaperProps: {
      style: {
        backgroundColor: theme.colors.background_white,
        color: theme.colors.secondary,
        maxHeight: 250,
      },
    },
  };

  const handleOnMouseOverCard = (row: number, column: number) => {
    setHasHoverCard(
      produce((draft) => {
        const newDraft = draft;
        newDraft[row][column] = false;
        return newDraft;
      }),
    );
  };
  const handleOnMouseOutCard = (row: number, column: number) => {
    setHasHoverCard(
      produce((draft) => {
        const newDraft = draft;
        newDraft[row][column] = false;
        return newDraft;
      }),
    );
  };

  const confirmRemoveClient = () => {
    if (solicitationsByClient.length > 0 && !clientId) return;
    const clientDeleted: IClientDeleteInputModel = {
      id: currentClientSelected.id,
      newClientSolicitationId: clientId,
      solicitationsIds: solicitationsByClient.map((s) => s.id as string),
    };
    onRequestRemoveClient(clientDeleted);
    reset({ id: '' });
  };

  const handleRequestClose = () => {
    onRequestClose();
    reset({ id: '' });
    setSolicitationsByClient([]);
  };

  useEffect(() => {
    if (currentClientSelected.id === '' || currentClientSelected.id === undefined) return;
    setSearchingSolicitation(true);
    CompanyController.getSolicitationsByClientId(currentClientSelected.id)
      .then((solicitations) => {
        setSolicitationsByClient(solicitations);
      })
      .finally(() => {
        setSearchingSolicitation(false);
      });
  }, [currentClientSelected.id]);

  useEffect(() => {
    setEnableClientsLoad(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleRequestClose}
      overlayClassName="react-modal-overlay"
      className="react-delete-modal-content">
      <Header>
        <div className="subHeader">
          <DeleteIcon />
          <h1>Exclusão</h1>
        </div>
        <div className="close-div">
          <button type="button" onClick={handleRequestClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </Header>
      <ContentModal>
        {searchingSolicitation ? (
          <Grid container justifyContent="center" alignContent="center">
            <Typography align="center" color={theme.colors.primary}>
              <CircularProgress size={15} /> Buscando...
            </Typography>
          </Grid>
        ) : (
          <>
            <div className="contentFlex">
              <h2 className="confirmationText">Tem certeza que quer excluir o cliente?</h2>
              <p>{currentClientSelected.name}</p>
            </div>
            {solicitationsByClient.length > 0 && (
              <>
                <h2 className="confirmationText">
                  Ele está vinculado a solicitações, você deseja alterar o cliente?
                </h2>
                <div>
                  <div className="divDropDownList">
                    <FormControl fullWidth>
                      <SelectControled
                        name="id"
                        control={control}
                        displayEmpty
                        classes={{ select: 'select-style' }}
                        value={clientId}
                        MenuProps={MenuProps}
                        IconComponent={ExpandArrowIcon}
                        itens={clients
                          .filter((c) => c.id !== currentClientSelected.id)
                          .map((client) => ({ id: client.id, value: client.name }))}
                        placeholderTitle="Selecione"
                      />
                    </FormControl>
                  </div>
                </div>
                <div className="contentSolicitations">
                  <p className="info">Solicitações abertas ou em andamento</p>
                  <div className="contentSolicitationOpened">
                    {solicitationsByClient.map((solicitation, index) => (
                      <CardOpenSolicitation
                        key={solicitation.id}
                        onMouseLeave={() => handleOnMouseOutCard(1 + index, index)}
                        onMouseEnter={() => {
                          handleOnMouseOverCard(1 + index, index + 1);
                        }}
                        zIndex={solicitationsByClient.length - index}
                        left={hasHoverCard[1][1] ? index * 91 : index * 15}
                        solicitation={solicitation}
                      />
                    ))}
                  </div>
                </div>
                <p className="confirmationText">
                  Ps.: Solicitações finalizadas permanecerão com o nome do cliente.
                </p>
              </>
            )}
            <div className="buttonModal">
              <Button
                text="Cancelar"
                dimensions={{ width: '10rem', height: '2.25rem' }}
                onClick={onRequestClose}
              />
              <Button
                text="Excluir"
                dimensions={{ width: '10rem', height: '2.25rem' }}
                onClick={confirmRemoveClient}
                disabled={solicitationsByClient.length > 0 && !clientId}
              />
            </div>
          </>
        )}
      </ContentModal>
    </Modal>
  );
}
